import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Spin, Empty, message, InputNumber, Select, Input } from 'antd';
import { DownloadOutlined, FilePdfOutlined, LeftOutlined, RightOutlined, EditOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useProducts, useAuth, useCart, ProductVariant, calculatePriceWithTier, getTierDiscountPercent } from '@hooks';
import { useCartDrawer } from '@contexts';
import { useState, useMemo, useEffect } from 'react';
import { ContactExpertsModal } from '@components/molecules';

const { Title, Paragraph } = Typography;
const { Option } = Select;

export const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProduct, updateVariant } = useProducts();
  const { isAuthenticated, profile } = useAuth();
  const isAdmin = profile.data?.profile === 'admin';
  const { addToCart } = useCart(isAuthenticated);
  const { openDrawer } = useCartDrawer();
  const [quantity, setQuantity] = useState(1);
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Variant selection state
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedChainSize, setSelectedChainSize] = useState<string | null>(null);
  
  // SKU editing state (admin only)
  const [isEditingSku, setIsEditingSku] = useState(false);
  const [editingSkuValue, setEditingSkuValue] = useState('');
  const [isSavingSku, setIsSavingSku] = useState(false);

  const { data: product, isLoading, error, refetch } = getProduct(id!);

  // Handle SKU edit save
  const handleSaveSku = async () => {
    if (!selectedVariant || !editingSkuValue.trim()) {
      setIsEditingSku(false);
      return;
    }
    
    setIsSavingSku(true);
    try {
      await updateVariant.mutateAsync({
        variantId: selectedVariant.id,
        data: { sku: editingSkuValue.trim() },
        productId: id!,
      });
      message.success('SKU updated successfully');
      setIsEditingSku(false);
      refetch();
    } catch (error) {
      message.error('Failed to update SKU');
    } finally {
      setIsSavingSku(false);
    }
  };

  const handleStartEditSku = () => {
    setEditingSkuValue(selectedVariant?.sku || '');
    setIsEditingSku(true);
  };
  
  // Get user tier (default to retail if not authenticated or no tier)
  const userTier = profile.data?.tier || 'retail';
  
  // Check if product has variants
  const hasVariants = useMemo(() => {
    return product?.variants && product.variants.length > 0;
  }, [product?.variants]);

  // Get unique sizes and chain sizes from variants
  const availableSizes = useMemo(() => {
    if (!product?.variants) return [];
    const sizes = [...new Set(product.variants.map((v: ProductVariant) => v.size))];
    return sizes.sort();
  }, [product?.variants]);

  const availableChainSizes = useMemo(() => {
    if (!product?.variants) return [];
    const chainSizes = [...new Set(product.variants.map((v: ProductVariant) => v.chainSize).filter(Boolean))] as string[];
    // Sort chain sizes numerically (smaller first)
    return chainSizes.sort((a, b) => {
      // Extract numeric value from strings like "9.5 MM" -> 9.5
      const numA = parseFloat(a.replace(/[^\d.]/g, '')) || 0;
      const numB = parseFloat(b.replace(/[^\d.]/g, '')) || 0;
      return numA - numB;
    });
  }, [product?.variants]);

  // Check if product has chain size variants
  const hasChainSizes = useMemo(() => {
    return availableChainSizes.length > 0;
  }, [availableChainSizes]);

  // Get selected variant
  const selectedVariant = useMemo(() => {
    if (!hasVariants || !selectedSize) return null;
    
    if (hasChainSizes) {
      // If product has chain sizes, require chain size selection
      if (!selectedChainSize) return null;
      return product?.variants?.find(
        (v: ProductVariant) => v.size === selectedSize && v.chainSize === selectedChainSize
      ) || null;
    } else {
      // If no chain sizes, find variant by size only
      return product?.variants?.find(
        (v: ProductVariant) => v.size === selectedSize && !v.chainSize
      ) || null;
    }
  }, [hasVariants, hasChainSizes, selectedSize, selectedChainSize, product?.variants]);

  // Check if variant combination is unavailable
  const isVariantUnavailable = useMemo(() => {
    return hasVariants && selectedSize && (hasChainSizes ? selectedChainSize : true) && !selectedVariant;
  }, [hasVariants, selectedSize, selectedChainSize, hasChainSizes, selectedVariant]);

  // Get base price from variants (highest price when no variant selected)
  const getBasePrice = useMemo(() => {
    if (product?.variants && product.variants.length > 0) {
      const prices = product.variants.map((v: ProductVariant) => Number(v.price) || 0);
      return Math.max(...prices);
    }
    return 0;
  }, [product?.variants]);

  // Calculate current price (variant price or base price) with tier discount
  const currentPrice = useMemo(() => {
    const basePrice = selectedVariant 
      ? Number(selectedVariant.price) 
      : getBasePrice;
    
    if (!isAuthenticated) return basePrice;
    return calculatePriceWithTier(basePrice, userTier);
  }, [selectedVariant, getBasePrice, isAuthenticated, userTier]);

  // Get original price (before tier discount)
  const originalPrice = useMemo(() => {
    return selectedVariant 
      ? Number(selectedVariant.price) 
      : getBasePrice;
  }, [selectedVariant, getBasePrice]);

  // Get current stock quantity (variant stock or total from all variants)
  const currentStockQuantity = useMemo(() => {
    if (selectedVariant) return selectedVariant.stockQuantity;
    // Sum all variant stocks when no specific variant is selected
    if (product?.variants && product.variants.length > 0) {
      return product.variants.reduce((total: number, v: ProductVariant) => total + (v.stockQuantity || 0), 0);
    }
    return 0;
  }, [selectedVariant, product?.variants]);

  // Get tier discount percentage
  const tierDiscountPercent = useMemo(() => {
    return getTierDiscountPercent(userTier);
  }, [userTier]);

  // Reset variant selection when product changes
  useEffect(() => {
    // Reset states when product changes
    setSelectedSize(null);
    setSelectedChainSize(null);
    setQuantity(1);
    setSelectedImageIndex(0);
  }, [id]); // Reset whenever the product ID changes

  // Initialize selected variant when product loads
  useEffect(() => {
    if (hasVariants && availableSizes.length > 0) {
      if (!selectedSize) setSelectedSize(availableSizes[0]);
      if (hasChainSizes && availableChainSizes.length > 0 && !selectedChainSize) {
        setSelectedChainSize(availableChainSizes[0]);
      }
    }
  }, [hasVariants, hasChainSizes, availableSizes, availableChainSizes, selectedSize, selectedChainSize]);


  const getProductImages = (product: any): string[] => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
    
    // Use images array if available
    if (product.images && product.images.length > 0) {
      return product.images.map((img: string) => 
        img.startsWith('http') ? img : `https://${bucketName}.s3.${region}.amazonaws.com/${img}`
      );
    }
    
    // Fallback to legacy imageUrl
    if (product.imageUrl) {
      const url = product.imageUrl.startsWith('http') 
        ? product.imageUrl 
        : `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
      return [url];
    }
    
    // Placeholder
    return [`https://placehold.co/600x400/181717/F74902?text=${encodeURIComponent(product.name)}`];
  };

  const getDocumentUrl = (fileUrl: string): string => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
    return fileUrl.startsWith('http') ? fileUrl : `https://${bucketName}.s3.${region}.amazonaws.com/${fileUrl}`;
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numPrice);
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!product) {
      message.error('Product not found');
      return;
    }

    // Check if variant is required but not selected
    if (hasVariants && !selectedVariant) {
      if (hasChainSizes) {
        message.error('Please select size and chain size');
      } else {
        message.error('Please select a size');
      }
      return;
    }

    // Check stock availability
    if (currentStockQuantity <= 0) {
      message.error('This product is currently out of stock');
      return;
    }

    // Check if quantity is valid
    if (quantity < 1) {
      message.error('Please select a valid quantity');
      return;
    }

    if (quantity > currentStockQuantity) {
      message.error(`Only ${currentStockQuantity} units available in stock`);
      return;
    }

    setIsAddingToCart(true);
    try {
      // Build product name with variant info if applicable
      let productName = product.name;
      if (selectedVariant) {
        if (hasChainSizes && selectedChainSize) {
          productName = `${product.name} (${selectedSize} / ${selectedChainSize})`;
        } else {
          productName = `${product.name} (${selectedSize})`;
        }
      }

      const productDetails = {
        name: productName,
        price: currentPrice,
        imageUrl: product.images && product.images.length > 0 ? product.images[0] : product.imageUrl || '',
        // Include variant info for order processing
        ...(selectedVariant && {
          variantId: selectedVariant.id,
          size: selectedSize,
          chainSize: selectedChainSize,
        }),
      };

      await addToCart(product.id, quantity, productDetails);
      
      // Open cart drawer after adding
      setTimeout(() => {
        openDrawer();
      }, 500);
    } catch (error) {
      console.error('Error adding to cart:', error);
      message.error('Error adding product to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Empty
          description={
            <span className="font-orator text-white">
              Product not found
            </span>
          }
        />
      </div>
    );
  }

  const productImages = getProductImages(product);

  return (
    <div className="min-h-screen bg-black text-white md:pt-3">
      {/* Product Details */}
      <section className="lg:py-18 bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Product Name and Item ID - aligned with description box */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-4">
            <div></div>
            <div className="flex justify-between items-end">
              <Title level={1} className="font-displayed !font-normal text-white uppercase !mb-0" style={{ fontSize: '3rem', lineHeight: '1' }}>
                {product?.name}
              </Title>
              {selectedVariant?.sku && (
                <div className="flex items-center gap-2">
                  {isAdmin && isEditingSku ? (
                    <div className="flex items-center gap-1">
                      <span className="font-orator text-orangicide text-sm tracking-wider">ITEM: #</span>
                      <Input
                        value={editingSkuValue}
                        onChange={(e) => setEditingSkuValue(e.target.value)}
                        onPressEnter={handleSaveSku}
                        onBlur={handleSaveSku}
                        autoFocus
                        size="small"
                        disabled={isSavingSku}
                        className="!w-24 !bg-transparent !border-b !border-t-0 !border-l-0 !border-r-0 !border-orangicide !rounded-none !text-orangicide font-orator text-sm !px-0 !shadow-none focus:!shadow-none"
                        style={{ background: 'transparent' }}
                      />
                      {isSavingSku ? (
                        <LoadingOutlined className="text-orangicide text-xs" />
                      ) : (
                        <CheckOutlined 
                          className="text-orangicide text-xs cursor-pointer hover:text-white transition-colors" 
                          onClick={handleSaveSku}
                        />
                      )}
                    </div>
                  ) : (
                    <span 
                      className={`font-orator text-orangicide text-sm tracking-wider whitespace-nowrap ${isAdmin ? 'cursor-pointer hover:text-white transition-colors group' : ''}`}
                      onClick={isAdmin ? handleStartEditSku : undefined}
                    >
                      {selectedVariant?.sku && `ITEM: #${selectedVariant.sku}`}
                      {isAdmin && selectedVariant?.sku && (
                        <EditOutlined className="ml-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Product Image Carousel */}
            <div>
              <div className="relative flex items-stretch gap-4">
                {/* (Left Arrow) */}
                {productImages.length > 1 && (
                  <div className="relative flex items-center justify-center w-8 sm:w-10 md:w-12 lg:w-16 group overflow-hidden">
                    <img 
                      src="/assets/frames/content-frame-faded-right.png" 
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover object-right opacity-50 group-hover:opacity-100 transition-opacity duration-300 z-0"
                    />
                    
                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev === 0 ? productImages.length - 1 : prev - 1))}
                      className="relative z-20 w-full h-full flex items-center justify-center transition-colors"
                      aria-label="Previous image"
                    >
                      <LeftOutlined className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl !text-zinc-400 group-hover:!text-white transition-colors drop-shadow-md cursor-pointer" />
                    </button>
                  </div>
                )}
                
                {/* Main Image */}
                <div className="relative aspect-square overflow-hidden bg-transparent flex-1">
                  <img
                    src={productImages[selectedImageIndex]}
                    alt={product?.name || 'Product'}
                    className="w-full h-full object-scale-down object-center relative z-10"
                    onError={(e) => {
                      e.currentTarget.src = `https://placehold.co/600x600/181717/F74902?text=${encodeURIComponent(product?.name || 'Product')}`;
                    }}
                  />
                </div>
                
                {/* --- (Right Arrow) --- */}
                {productImages.length > 1 && (
                  <div className="relative flex items-center justify-center w-8 sm:w-10 md:w-12 lg:w-16 group cursor-pointer overflow-hidden">
                    <img 
                      src="/assets/frames/content-frame-faded-left.png" 
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover object-left opacity-50 group-hover:opacity-100 transition-opacity duration-300 z-0"
                    />

                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev === productImages.length - 1 ? 0 : prev + 1))}
                      className="relative z-20 w-full h-full flex items-center justify-center transition-colors"
                      aria-label="Next image"
                    >
                      <RightOutlined className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl !text-zinc-400 group-hover:!text-white transition-colors drop-shadow-md cursor-pointer" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Carousel Indicators - below image */}
              {productImages.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {productImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`h-3 rounded-full transition-all ${
                        selectedImageIndex === index ? 'w-6' : 'w-3'
                      }`}
                      style={{
                        backgroundColor: '#555555'
                      }}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              {/* Description Box */}
                <div className="relative mb-6">
                  <div 
                    className="relative border-4 p-6"
                    style={{ 
                      clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                      borderColor: '#D9D9D9'
                    }}
                  >
                    {/* Corner decorations */}
                    <div
                      className="pointer-events-none absolute top-0 left-0"
                      style={{ 
                        width: 20, 
                        height: 20, 
                        clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                        backgroundColor: '#D9D9D9'
                      }}
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 right-0"
                      style={{ 
                        width: 20, 
                        height: 20, 
                        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                        backgroundColor: '#D9D9D9'
                      }}
                    />
                    <Paragraph className="font-orator text-white text-sm leading-relaxed !mb-0 uppercase relative z-10">
                      {product?.description || 'INFO AND DETAIL SECTION TO WRITE BRIEFLY ABOUT PRODUCT SIZES, OR OTHER IMPORTANT INFO THAT PEOPLE WILL NEED TO KNOW TO BUY THE RIGHT PRODUCT.'}
                    </Paragraph>
                  </div>
                </div>

              {/* Size Selector */}
              {hasVariants && availableSizes.length > 0 && (
                <div className="bg-white py-2 px-3 flex items-center gap-4">
                  <label className="font-orator text-black text-lg whitespace-nowrap">SIZE:</label>
                  <Select
                    value={selectedSize}
                    onChange={setSelectedSize}
                    className="flex-1"
                    size="large"
                    rootClassName="product-select-inner"
                    bordered={false}
                  >
                    {availableSizes.map((size) => (
                      <Option key={size} value={size}>{size}</Option>
                    ))}
                  </Select>
                </div>
              )}

              {/* Chain Size Selector - only if product has chain sizes */}
              {hasChainSizes && availableChainSizes.length > 0 && (
                <div className="bg-white py-2 px-3 flex items-center gap-4">
                  <label className="font-orator text-black text-lg whitespace-nowrap">CHAIN SIZE:</label>
                  <Select
                    value={selectedChainSize}
                    onChange={setSelectedChainSize}
                    className="flex-1"
                    size="large"
                    rootClassName="product-select-inner"
                    bordered={false}
                  >
                    {availableChainSizes.map((chainSize) => (
                      <Option key={chainSize} value={chainSize}>{chainSize}</Option>
                    ))}
                  </Select>
                </div>
              )}

              {/* Variant not found warning */}
              {isVariantUnavailable && (
                <div className="font-orator text-orangicide text-sm">
                  This combination is not available. Please select a different option.
                </div>
              )}

              {/* Quantity Selector */}
              {!isVariantUnavailable && isAuthenticated && currentStockQuantity > 0 && (!hasVariants || selectedVariant) && (
                <div className="bg-white py-2 px-3 flex items-center gap-4">
                  <label className="font-orator text-black text-lg whitespace-nowrap">QTY:</label>
                  <InputNumber
                    min={1}
                    max={currentStockQuantity || 1}
                    value={quantity}
                    onChange={(value) => setQuantity(value || 1)}
                    className="quantity-input-white font-orator text-xl font-bold flex-1"
                    style={{ 
                      width: '100%',
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      color: 'black'
                    }}
                    controls={false}
                  />
                  <span className="font-orator text-black text-lg font-bold uppercase">PCS</span>
                </div>
              )}

              {/* Stock Availability */}
              {!isVariantUnavailable && isAuthenticated && (
                <div>
                  {currentStockQuantity > 0 ? (
                    <div className="font-orator text-sm">
                      <span className="text-zinc-400">Availability: </span>
                      <span className={
                        currentStockQuantity > 10 
                          ? 'text-green-500' 
                          : currentStockQuantity > 0 
                            ? 'text-orangicide' 
                            : 'text-red-500'
                      }>
                        {currentStockQuantity > 10 
                          ? 'In Stock' 
                          : `Only ${currentStockQuantity} left!`}
                      </span>
                    </div>
                  ) : (
                    <div className="font-orator text-sm text-red-500">
                      Out of Stock
                    </div>
                  )}
                </div>
              )}

              {/* Price and Add to Cart */}
              {!isVariantUnavailable && (
                <div className={`flex ${!isAuthenticated ? 'flex-col items-start gap-0' : 'items-center justify-between gap-4'} pt-4`}>
                  {/* Price Display */}
                  {isAuthenticated && (
                  <div className="flex items-center gap-3">
                    <div className="font-displayed text-white !font-bold text-4xl">
                      {formatPrice(currentPrice * quantity)}
                    </div>
                    
                    {/* Show original price and discount if user has tier discount */}
                    {tierDiscountPercent > 0 && (
                      <div className="font-displayed text-zinc-500 line-through !font-bold text-4xl">
                        {formatPrice(originalPrice * quantity)}
                      </div>
                    )}
                  </div>
                )}

                {/* Additional Info Messages */}
                {!isAuthenticated && (
                  <Paragraph className="font-orator text-white text-sm">
                    Please log in to add products to cart
                  </Paragraph>
                )}

                {/* Add to Cart Button */}
                <Button
                  type="primary"
                  size="large"
                  className={`btn-orangicide font-displayed !font-normal !text-2xl w-40 !h-10 flex items-center justify-center ${!isAuthenticated ? 'self-start' : 'self-center'}`}
                  onClick={handleAddToCart}
                  disabled={isAuthenticated && (currentStockQuantity <= 0 || (hasVariants && !selectedVariant))}
                  loading={isAddingToCart}
                >
                  {!isAuthenticated 
                    ? 'LOGIN' 
                    : hasVariants && !selectedVariant
                      ? 'SELECT OPTIONS'
                      : currentStockQuantity <= 0 
                        ? 'OUT OF STOCK' 
                        : isAddingToCart
                          ? 'ADDING...'
                          : 'ADD TO CART'}
                </Button>
                </div>
              )}

              {!isVariantUnavailable && isAuthenticated && currentStockQuantity <= 0 && (!hasVariants || selectedVariant) && (
                <Paragraph className="font-orator text-red-400 text-sm">
                  This product is currently out of stock
                </Paragraph>
              )}
              {!isVariantUnavailable && isAuthenticated && hasVariants && !selectedVariant && (
                <Paragraph className="font-orator text-orangicide text-sm">
                  {hasChainSizes ? 'Please select size and chain size' : 'Please select a size'}
                </Paragraph>
              )}

              {/* Tier Discount Info */}
              {!isVariantUnavailable && isAuthenticated && tierDiscountPercent > 0 && (
                <div className="font-orator text-zinc-400 text-sm pt-2">
                  Your {userTier.replace('tier', 'Tier ')} discount ({tierDiscountPercent}% OFF) applied
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      {product?.videoUrl && (
        <section className="py-16 bg-[url(/assets/images/Background.png)] bg-center bg-cover">
          <div className="container mx-auto px-4">
            <Title level={2} className="font-displayed text-white uppercase mb-12 text-center tracking-widest" style={{ fontSize: '3rem' }}>
              Product Video
            </Title>
            <div className="max-w-4xl mx-auto">
              <div className="relative pb-[56.25%] h-0 overflow-hidden">
                <iframe
                  src={product.videoUrl}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Product Video"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Technical Documents Section */}
      {product?.documents && product.documents.length > 0 && (
        <section className="py-16 bg-[url(/assets/images/Background.png)] bg-center bg-cover">
          <div className="container mx-auto px-4">
            <Title level={2} className="font-displayed text-white uppercase mb-12 text-center tracking-widest" style={{ fontSize: '3rem' }}>
              Technical Documents
            </Title>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {product.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="relative cursor-pointer transition-all hover:scale-105"
                  onClick={() => window.open(getDocumentUrl(doc.fileUrl), '_blank')}
                >
                  <div 
                    className="relative bg-black border-4 border-white flex flex-col items-center p-8 text-center h-full"
                    style={{
                      clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)'
                    }}
                  >
                    {/* Corner decorations */}
                    <div
                      className="pointer-events-none absolute top-0 left-0"
                      style={{ 
                        width: 20, 
                        height: 20, 
                        clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                        backgroundColor: 'white'
                      }}
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 right-0"
                      style={{ 
                        width: 20, 
                        height: 20, 
                        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                        backgroundColor: 'white'
                      }}
                    />
                    <FilePdfOutlined className="text-5xl text-orangicide mb-4 relative z-10" />
                    <Title level={5} className="font-orator text-white mb-2 relative z-10">
                      {doc.name}
                    </Title>
                    {doc.description && (
                      <Paragraph className="font-orator !text-gray-400 !text-sm !mb-4 relative z-10">
                        {doc.description}
                      </Paragraph>
                    )}
                    <Button
                      icon={<DownloadOutlined />}
                      className="btn-orangicide font-orator relative z-10 uppercase"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(getDocumentUrl(doc.fileUrl), '_blank');
                      }}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Products Section */}
      {product?.relatedProducts && product.relatedProducts.length > 0 && (
        <section className="py-16 bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat">
          <div className="container mx-auto px-4">
            <Title level={2} className="font-displayed text-white uppercase mb-12 text-center tracking-widest">
              Related Products
            </Title>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {product.relatedProducts.map((relatedProduct: any) => {
                const relatedImages = relatedProduct.images && relatedProduct.images.length > 0 
                  ? relatedProduct.images 
                  : relatedProduct.imageUrl ? [relatedProduct.imageUrl] : [];
                const relatedImageUrl = relatedImages.length > 0 
                  ? (relatedImages[0].startsWith('http') 
                      ? relatedImages[0] 
                      : `https://${import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production'}.s3.${import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2'}.amazonaws.com/${relatedImages[0]}`)
                  : `https://placehold.co/400x300/181717/F74902?text=${encodeURIComponent(relatedProduct.name)}`;
                
                return (
                  <div
                    key={relatedProduct.id}
                    className="cursor-pointer transition-all hover:scale-105"
                    onClick={() => {
                      navigate(`/merch/${relatedProduct.id}`);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {/* Product Image with Gradient */}
                    <div className="relative aspect-square overflow-hidden bg-transparent mb-4">
                      <img 
                        alt={relatedProduct.name} 
                        src={relatedImageUrl}
                        className="w-full h-full object-scale-down object-center relative z-10"
                        onError={(e) => {
                          e.currentTarget.src = `https://placehold.co/400x300/181717/F74902?text=${encodeURIComponent(relatedProduct.name)}`;
                        }}
                      />
                      {/* Gradient overlay - solid at bottom, fading up */}
                      <div 
                        className="absolute inset-0 pointer-events-none z-0"
                        style={{
                          background: 'linear-gradient(to top, rgba(247, 73, 2, 0.5) 0%, rgba(247, 73, 2, 0.2) 40%, rgba(247, 73, 2, 0) 85%)'
                        }}
                      />
                    </div>
                    
                    {/* Product Name with Border */}
                    <div className="relative">
                      <div 
                        className="relative border-4 pt-6 px-6 pb-0"
                        style={{ 
                          clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                          borderColor: '#D9D9D9'
                        }}
                      >
                        {/* Corner decorations */}
                        <div
                          className="pointer-events-none absolute top-0 left-0"
                          style={{ 
                            width: 20, 
                            height: 20, 
                            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                            backgroundColor: '#D9D9D9'
                          }}
                        />
                        <div
                          className="pointer-events-none absolute bottom-0 right-0"
                          style={{ 
                            width: 20, 
                            height: 20, 
                            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                            backgroundColor: '#D9D9D9'
                          }}
                        />
                        <h3 className="font-orator text-white text-center text-sm uppercase tracking-wider !mb-2 relative z-10">
                          {relatedProduct.name}
                        </h3>
                        {relatedProduct.category && (
                          <Paragraph className="font-orator text-orangicide mb-0 text-center text-xs relative z-10">
                            {relatedProduct.category}
                          </Paragraph>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Request a Quote Section */}
      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat py-20 relative">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center items-center gap-4 md:gap-6 mb-12">
          <img 
                src="/assets/frames/need-help-frame-faded.svg"
                alt="Need Help Frame"
                className="max-w-full md:max-w-5xl h-auto object-cover lg:mt-16 [mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_10%,black_15%,black_85%,transparent_90%)]"
              />
          </div>

          <Title level={1} className="font-displayed !font-normal text-white uppercase !mb-0">
            NEED HELP FINDING THE RIGHT PRODUCT?
          </Title>
          <Paragraph className="font-orator text-orangicide max-w-3xl tracking-wider mx-auto mt-6 !mb-12 !text-xl lg:!text-2xl uppercase">
            OUR TEAM IS AVAILABLE TO ASSIST YOU IN SELECTING THE PERFECT GEAR FOR YOUR MACHINES.
          </Paragraph>
          <button onClick={() => setIsContactModalVisible(true)} className="btn-orangicide font-displayed text-xl md:text-2xl !w-auto px-8 py-4 tracking-wider cursor-pointer uppercase">
            CONTACT OUR EXPERTS
          </button>
        </div>
      </section>

      {/* Our Family of Brands Section */}
      <section className="bg-zinc-950 pb-12">
        <div className="container mx-auto">
          <div className="font-displayed !font-normal text-white text-center text-4xl md:text-5xl uppercase py-12">
            OUR FAMILY OF BRANDS
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 pb-6">
            {/* Logos das marcas */}
            <a 
              href="https://ruppertinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/RuppertLogo.svg" 
                alt="Ruppert Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://estabrookinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/EstabrookLogo.svg" 
                alt="Estabrook Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://modlink.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/ModLinkLogo.svg" 
                alt="Modlink Logo" 
                className="max-w-full max-h-full"
              />
            </a>
            <div className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center">
              <img 
                src="/assets/logos/SnowSyncLogo.svg" 
                alt="Snow Sync Logo" 
                className="max-w-full max-h-full" 
              />
            </div>
            <div className="w-[100px] md:w-[150px] h-[70px] md:mt-2 flex items-center justify-center">
              <img 
                src="/assets/logos/asrLogo.svg" 
                alt="ASR Logo" 
                className="max-w-full max-h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Experts Modal */}
      <ContactExpertsModal
        visible={isContactModalVisible}
        onClose={() => setIsContactModalVisible(false)}
        productName={product?.name}
        source="product-detail"
      />
    </div>
  );
};
